import {
  defaultCloseTime,
  defaultOpenTime,
  emailRegExpMatcher,
  militaryToStandardTime,
} from '@fv/client-core'
import type {
  BaseClientAddress,
  ClientAddressLookupResult,
} from '@fv/client-types'
import { type AddressUpsertDTO, CountryMapper, type Workflow } from '@fv/models'

import {
  mapAddressBookToShipmentLocationFormModel,
  type ShipmentLocationFormModel,
} from '@/features/shipment-location-form/types'

import type { FormAddress } from './types'

export type PartialLocation = Partial<
  Omit<BaseClientAddress, 'lat' | 'lng' | 'timezone'>
>
export type AddressWithCompany = PartialLocation & { company?: string }

export const addressMapper = {
  dtoToFormStop: (
    location: ClientAddressLookupResult | null,
    workflow: Workflow,
  ): Partial<ShipmentLocationFormModel> => {
    return mapAddressBookToShipmentLocationFormModel(location, workflow)
  },
  formAddressToDTO: (a: FormAddress): AddressUpsertDTO => {
    const adminArea3 = a.adminArea3?.trim()
    const contactEmail = a.contactEmail.trim()
    const contactName = a.contactName.trim()
    const contactPhone = a.contactPhone.trim()
    const contactPhoneExt = a.contactPhoneExt.trim()
    const opensAt = a.opensAt?.trim()
    const closesAt = a.closesAt?.trim()

    return {
      address: a.address.trim(),
      address2: a.address2.trim(),
      bolFollowers: a.bolFollowers.match(emailRegExpMatcher) || [],
      city: a.city.trim(),
      company: a.company.trim(),
      country: CountryMapper.coreToLegacy(a.country),
      flatbedHandlingType: a.flatbedHandlingType || undefined,
      instructions: a.instructions.trim(),
      internalNotes: a.internalNotes?.trim(),
      mappedAccessorials: a.mappedAccessorials,
      postalCode: a.postalCode.trim(),
      sendBOL: !!a.sendBOL,
      shipType: a.shipType === '' ? 'business dock' : a.shipType,
      state: a.state.trim(),
      dockHoursStart: opensAt ? militaryToStandardTime(opensAt) : '',
      dockHoursEnd: closesAt ? militaryToStandardTime(closesAt) : '',
      type: a.type || 'shipping',
      ...(adminArea3 && { adminArea3 }),
      ...(contactEmail && { contactEmail }),
      ...(contactName && { contactName }),
      ...(contactPhone && { contactPhone }),
      ...(contactPhoneExt && { contactPhoneExt }),
    }
  },
  buildFormAddress: (v?: ClientAddressLookupResult): FormAddress => {
    return {
      address: v?.address || '',
      address2: v?.address2 || '',
      bolFollowers: v?.bolFollowers?.join(', ') || '',
      city: v?.city || '',
      closesAt: v ? v.dockHoursEnd : defaultCloseTime,
      company: v?.company || '',
      contactEmail: v?.contactEmail || '',
      contactName: v?.contactName || '',
      contactPhone: v?.contactPhone || '',
      contactPhoneExt: v?.contactPhoneExt || '',
      country: v?.country || 'us',
      flatbedHandlingType: v?.flatbedHandlingType,
      instructions: v?.instructions || '',
      internalNotes: v?.internalNotes || '',
      isAccountLocation: !!v?.isAccountLocation,
      mappedAccessorials: v?.mappedAccessorials ?? {
        ltl: [],
        parcel: [],
        truckload: [],
      },
      postalCode: v?.postalCode || '',
      opensAt: v ? v.dockHoursStart : defaultOpenTime,
      sendBOL: !!v?.sendBOL,
      shipType: v?.shipType || 'business dock',
      state: v?.state || '',
    }
  },
}
